import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandMark, EventismCredit } from "@/components/Brand";
import { toast } from "sonner";

export default function Survey() {
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  const [rating, setRating] = useState(0);
  const [podClean, setPodClean] = useState(true);
  const [pinWorked, setPinWorked] = useState(true);
  const [nps, setNps] = useState(null);
  const [comment, setComment] = useState("");
  const [wishlist, setWishlist] = useState("");
  const [eventType, setEventType] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get(`/surveys/${bookingId}`)
      .then(({ data }) => {
        setBooking(data.booking);
        setSubmitted(data.submitted);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Booking not found"))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const submit = async () => {
    if (rating === 0) {
      toast.error("Please give us a star rating first.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/surveys/${bookingId}`, {
        rating,
        pod_clean: podClean,
        pin_worked: pinWorked,
        nps,
        comment: comment || null,
        equipment_wishlist: wishlist
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 8),
        event_type: eventType || null,
      });
      setDone(true);
      toast.success("Thanks for the feedback!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-slate flex items-center justify-center">
        <i className="fas fa-spinner fa-spin text-brand-blue text-3xl" />
      </div>
    );
  }
  if (error) {
    return (
      <CenteredCard
        title="Survey not available"
        body={error}
        cta="Back home"
        href="/"
      />
    );
  }
  if (submitted || done) {
    return (
      <CenteredCard
        title={done ? "Thanks for the feedback!" : "Already submitted"}
        body={
          done
            ? "We read every response. The Pod gets better because of you."
            : "Looks like you already filled this out — appreciate it!"
        }
        cta="Browse Pod"
        href="/#inventory"
        testId="survey-done"
      />
    );
  }

  return (
    <div className="min-h-screen bg-brand-slate flex flex-col">
      <div className="px-6 py-5 flex justify-between items-center border-b border-slate-100 bg-white">
        <BrandMark />
        <Link
          to="/"
          className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-brand-red"
          data-testid="survey-back-link"
        >
          <i className="fas fa-arrow-left mr-2" /> Back
        </Link>
      </div>

      <main className="flex-1 px-4 py-10">
        <div className="max-w-2xl mx-auto" data-testid="survey-page">
          <div className="bg-brand-blue text-white rounded-[28px] p-7 mb-6">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-2">
              Booking #{(booking?.id || "").slice(0, 8).toUpperCase()}
            </p>
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tightest">
              How'd we do?
            </h1>
            <p className="text-white/80 text-sm font-bold mt-2">
              Two minutes. Helps us pick what gear to add next — and what's working.
            </p>
          </div>

          <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-7 space-y-6">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue mb-3">
                Overall Rating
              </p>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl transition-colors ${
                      n <= rating
                        ? "bg-brand-gold text-brand-blue"
                        : "bg-slate-100 text-slate-300 hover:bg-slate-200"
                    }`}
                    data-testid={`survey-rating-${n}`}
                  >
                    <i className="fas fa-star" />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <YesNo
                label="Pod was clean & stocked"
                value={podClean}
                onChange={setPodClean}
                testId="survey-clean"
              />
              <YesNo
                label="PIN unlocked first try"
                value={pinWorked}
                onChange={setPinWorked}
                testId="survey-pin"
              />
            </div>

            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue mb-3">
                Would you recommend us? (0–10)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                  <button
                    key={n}
                    onClick={() => setNps(n)}
                    className={`w-9 h-9 rounded-lg text-xs font-black transition-colors ${
                      nps === n
                        ? "bg-brand-blue text-white"
                        : "bg-slate-100 text-brand-blue hover:bg-slate-200"
                    }`}
                    data-testid={`survey-nps-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
                Equipment we should add (one per line)
              </label>
              <textarea
                value={wishlist}
                onChange={(e) => setWishlist(e.target.value)}
                rows={3}
                placeholder={"e.g. Cocktail bar\nDance floor\nKaraoke machine"}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 resize-none"
                data-testid="survey-wishlist"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
                What kind of event was this?
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
                data-testid="survey-event-type"
              >
                <option value="">— pick one —</option>
                <option value="wedding">Wedding</option>
                <option value="birthday">Birthday</option>
                <option value="graduation">Graduation</option>
                <option value="corporate">Corporate</option>
                <option value="church-school">Church / School</option>
                <option value="bbq-backyard">BBQ / Backyard</option>
                <option value="dinner-party">Dinner Party</option>
                <option value="house-party">House Party</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
                Anything else? (optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="What worked, what didn't, what'd make it perfect?"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 resize-none"
                data-testid="survey-comment"
              />
            </div>

            <button
              onClick={submit}
              disabled={busy || rating === 0}
              className="btn-primary w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm disabled:opacity-50"
              data-testid="survey-submit-button"
            >
              {busy ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2" /> Sending
                </>
              ) : (
                <>
                  Submit Feedback <i className="fas fa-paper-plane ml-2" />
                </>
              )}
            </button>
          </div>

          <div className="mt-8 text-center">
            <EventismCredit />
          </div>
        </div>
      </main>
    </div>
  );
}

function YesNo({ label, value, onChange, testId }) {
  return (
    <div className="rounded-2xl border-2 border-slate-200 p-4">
      <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue mb-2">
        {label}
      </p>
      <div className="flex gap-2">
        {[
          { v: true, label: "Yes" },
          { v: false, label: "No" },
        ].map((o) => (
          <button
            key={o.label}
            onClick={() => onChange(o.v)}
            className={`flex-1 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-colors ${
              value === o.v
                ? "bg-brand-blue text-white"
                : "bg-slate-100 text-brand-blue hover:bg-slate-200"
            }`}
            data-testid={`${testId}-${o.label.toLowerCase()}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CenteredCard({ title, body, cta, href, testId }) {
  return (
    <div className="min-h-screen bg-brand-slate flex items-center justify-center px-4">
      <div
        className="bg-white rounded-[32px] p-10 text-center border border-slate-100 shadow-sm max-w-md"
        data-testid={testId}
      >
        <div className="w-16 h-16 mx-auto rounded-3xl bg-brand-blue text-brand-gold flex items-center justify-center mb-5">
          <i className="fas fa-circle-check text-3xl" />
        </div>
        <h1 className="text-2xl font-black uppercase tracking-tightest text-brand-blue mb-2">
          {title}
        </h1>
        <p className="text-slate-500 font-bold text-sm mb-6">{body}</p>
        <Link
          to={href}
          className="btn-primary inline-block px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest"
        >
          {cta}
        </Link>
      </div>
    </div>
  );
}
